import { useEffect, useRef } from 'react';
import { playGameSound } from '../audio/gameAudio';

import { GAME_STATE_KEYS } from '../games/gameStateKeys';

function describeRoomAudioState(roomId, room) {
  const gameStateKey = GAME_STATE_KEYS[room.gameType];
  const gameState = gameStateKey ? room[gameStateKey] || {} : {};
  return {
    roomId,
    gameType: room.gameType,
    status: room.status,
    round: room.currentRound ?? gameState.currentHoleIdx ?? gameState.currentQuestionIndex,
    phase: gameState.phase,
    turn: room.currentDrawer ?? gameState.currentTurn ?? gameState.currentTurnIndex ??
      gameState.currentIndex ?? gameState.currentPlayer ?? gameState.currentPlayerId,
    dice: gameState.diceValue ?? gameState.lastRoll,
    winner: gameState.winner,
    lastAction: JSON.stringify(gameState.lastAction || null),
    activity: room.gameType === 'boggle' ? null : JSON.stringify(
      gameState.positions || gameState.pieces || gameState.balls ||
      gameState.submissions || gameState.answers ||
      gameState.discardPile?.length || gameState.turnCount || null
    ),
    scores: JSON.stringify(gameState.scores || Object.values(room.players || {}).map(player => player.score || 0)),
  };
}

/** Plays shared sound cues when the room's game state changes between snapshots. */
export function useRoomAudioCues(roomId, room) {
  const previousAudioState = useRef(null);

  useEffect(() => {
    if (!roomId || !room) {
      previousAudioState.current = null;
      return;
    }

    const current = describeRoomAudioState(roomId, room);
    const previous = previousAudioState.current;
    previousAudioState.current = current;
    if (!previous || previous.roomId !== roomId || previous.gameType !== room.gameType) return;
    if (previous.status !== current.status) {
      if (current.status === 'finished' || current.winner) playGameSound('win');
      else if (current.status === 'playing') playGameSound('start');
      else if (current.status === 'roundEnd') playGameSound('move');
      return;
    }
    if (previous.round !== current.round) {
      playGameSound('start');
    } else if (previous.winner !== current.winner && current.winner) {
      playGameSound('win');
    } else if (previous.dice !== current.dice && current.dice != null) {
      playGameSound('roll');
    } else if (previous.lastAction !== current.lastAction && current.lastAction) {
      playGameSound('card');
    } else if (previous.scores !== current.scores) {
      playGameSound('correct');
    } else if (previous.phase !== current.phase && current.phase) {
      playGameSound('start');
    } else if (previous.activity !== current.activity) {
      playGameSound(room.gameType === 'uno' ? 'card' : 'move');
    } else if (previous.turn !== current.turn && current.turn != null) {
      playGameSound('move');
    }
  }, [roomId, room]);
}
